import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { formatDate } from "../utils/helpers";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { getRazorpayKeyId } from "../api/configHelper";
import { CheckCircle, ChevronDown, Home as HomeIcon, MapPin, Pencil, Plus, ShoppingBag, Trash2, Truck, X } from "lucide-react";
import { formatMealType, getCartRestrictionForAddress, MEAL_TYPES } from "../utils/rainAvailability";

const getAddressLineValue = (address) =>
  [address?.Address1 || address?.Address || address?.AddressLine, address?.Address2]
    .filter((part) => part && String(part).trim())
    .join(", ");

const getAddressCityValue = (address) => (address?.City || address?.Area || "").trim();
const getAddressDivisionValue = (address) => (address?.Division || address?.Route || "").trim();
const getAddressIdValue = (address) =>
  Number(address?.AId || address?.Aid || address?.AID || address?.aId || address?.AddressId || address?.Id || address?.id || 0);

const getAddressIdentityKey = (address) =>
  [
    address?.ShortName,
    getAddressLineValue(address),
  ]
    .map((part) => String(part || "").trim().toLowerCase())
    .join("|");

const getAddressDedupeKeys = (address) => {
  const keys = [];
  const addressId = getAddressIdValue(address);
  if (addressId) keys.push(`id:${addressId}`);
  keys.push(`identity:${getAddressIdentityKey(address)}`);
  return keys;
};

const dedupeAddresses = (addresses) => {
  const seen = new Set();
  return addresses.filter((address) => {
    const keys = getAddressDedupeKeys(address);
    if (keys.some((key) => seen.has(key))) return false;
    keys.forEach((key) => seen.add(key));
    return true;
  });
};

export default function Cart() {
  const { cart, calculateTotals, user, placeOrder, addToCart, isTakeaway, setIsTakeaway, findMenuItem, setUser, setCart } = useApp();
  const navigate = useNavigate();

  const [distanceKey, setDistanceKey] = useState(user?.addressRange || "0-5");
  const [deliveryAddress, setDeliveryAddress] = useState(user?.address || "");
  const [paymentMethod, setPaymentMethod] = useState(user?.IsCodAvail === 1 ? "cod" : "upi");
  const [deliveryRemarks, setDeliveryRemarks] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomerInactive, setIsCustomerInactive] = useState(false);
  const [additionalAddresses, setAdditionalAddresses] = useState([]);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isOrderTypeOpen, setIsOrderTypeOpen] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [addressRestrictionModal, setAddressRestrictionModal] = useState(null);

  const selectTakeaway = () => {
    setIsTakeaway(true);
    setIsOrderTypeOpen(false);
  };

  const selectDelivery = () => {
    setIsTakeaway(false);
    setIsOrderTypeOpen(false);
    setIsAddressModalOpen(true);
  };

  const getAddressLine = getAddressLineValue;
  const getAddressCity = getAddressCityValue;
  const getAddressDivision = getAddressDivisionValue;
  const getAddressKey = (address, index, prefix = "additional") =>
    `${prefix}-${address?.Vid || user?.Vid || "user"}-${address?.ShortName || "address"}-${index}`;

  const validateAddressCartItems = async (address) => {
    const cartDates = Object.keys(cart || {});
    if (!cartDates.length) return { affectedItems: [], blockedMealTypes: [] };

    const restrictionRowsByDate = {};
    const restrictionContentRowsByDate = {};
    await Promise.all(
      cartDates.map(async (date) => {
        const res = await api.GetMenuplan(date);
        restrictionRowsByDate[date] = res?.GetMenuplanjs?.Table3 || [];
        restrictionContentRowsByDate[date] = res?.GetMenuplanjs?.Table4 || [];
      })
    );

    return getCartRestrictionForAddress({
      cart,
      findMenuItem,
      restrictionRowsByDate,
      restrictionContentRowsByDate,
      address,
    });
  };

  const blockAddressChangeWithCartItems = (address, restrictionCheck, updatedUser, addressText) => {
    setAddressRestrictionModal({
      addressName: address?.ShortName || "selected address",
      addressArea: getAddressCity(address),
      affectedItems: restrictionCheck.affectedItems,
      blockedMealTypes: restrictionCheck.blockedMealTypes,
      wording: restrictionCheck.wording,
      updatedUser,
      addressText,
    });
    setIsAddressModalOpen(false);
  };

  const persistSelectedUser = (updatedUser, addressText) => {
    setUser(updatedUser);
    sessionStorage.setItem("user", JSON.stringify(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setDeliveryAddress(addressText);
    setIsAddressModalOpen(false);
  };

  const removeBlockedItemsAndUseAddress = () => {
    if (!addressRestrictionModal?.updatedUser) return;

    const blockedItemKeys = new Set(
      addressRestrictionModal.affectedItems.map((item) => `${item.date}|${item.itemId}`)
    );

    setCart((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([date, itemsObj]) => {
        const keptItems = Object.fromEntries(
          Object.entries(itemsObj || {}).filter(([itemId]) => !blockedItemKeys.has(`${date}|${itemId}`))
        );
        if (Object.keys(keptItems).length) next[date] = keptItems;
      });
      return next;
    });

    persistSelectedUser(addressRestrictionModal.updatedUser, addressRestrictionModal.addressText || "");
    setAddressRestrictionModal(null);
  };

  const applySelectedAddress = async (address, key, isPrimary = false) => {
    if (isPrimary) {
      const addressLine = getAddressLine(address);
      const city = getAddressCity(address);
      const division = getAddressDivision(address);
      const addressText = `${addressLine}${city ? ", " + city : ""}`.trim();

      const updatedUser = {
        ...user,
        address: addressLine,
        landmark: address?.Address2?.trim?.() || "",
        area: city || user?.area || "",
        division: division || user?.division || "",
        IsCodAvail: address?.IsCodAvail ?? user?.IsCodAvail ?? 0,
        selectedAdditionalAddress: null,
      };

      const restrictionCheck = await validateAddressCartItems(address);
      if (restrictionCheck.affectedItems.length) {
        blockAddressChangeWithCartItems(address, restrictionCheck, updatedUser, addressText);
        return;
      }

      persistSelectedUser(updatedUser, addressText);
      return;
    }

    const selectedAddress = {
      ...address,
      selectedKey: key,
      AddressLine: getAddressLine(address),
      City: getAddressCity(address),
      Division: getAddressDivision(address),
      isPrimary,
    };
    const addressText = `${selectedAddress.AddressLine}${selectedAddress.City ? ", " + selectedAddress.City : ""}`.trim();

    const updatedUser = {
      ...user,
      address: selectedAddress.AddressLine,
      landmark: address?.Address2?.trim?.() || "",
      area: selectedAddress.City || user?.area || "",
      division: selectedAddress.Division || user?.division || "",
      IsCodAvail: address?.IsCodAvail ?? user?.IsCodAvail ?? 0,
      selectedAdditionalAddress: selectedAddress,
    };

    const restrictionCheck = await validateAddressCartItems(selectedAddress);
    if (restrictionCheck.affectedItems.length) {
      blockAddressChangeWithCartItems(selectedAddress, restrictionCheck, updatedUser, addressText);
      return;
    }

    persistSelectedUser(updatedUser, addressText);
  };

  const getAddressId = getAddressIdValue;

  const resolveAddressId = (address) => {
    const directId = getAddressId(address);
    if (directId) return directId;

    const addressKey = getAddressIdentityKey(address);
    const savedMatch = additionalAddresses.find((item) => getAddressIdentityKey(item) === addressKey && getAddressId(item));
    return getAddressId(savedMatch);
  };

  const handleEditAddress = async (event, address) => {
    event.stopPropagation();
    const addressId = resolveAddressId(address);
    if (!addressId || !user?.Vid) {
      alert("Address ID is missing. Please refresh and try again.");
      return;
    }

    try {
      const res = await api.GetDeleteCustomerAdd(user.Vid, addressId, "edit");
      const editAddress =
        res?.data?.DeleteCustomerAddjs?.Table?.[0] ||
        res?.DeleteCustomerAddjs?.Table?.[0] ||
        address;

      navigate("/add-additional-address", {
        state: {
          mode: "edit",
          address: editAddress,
        },
      });
    } catch (err) {
      console.error("Failed to fetch address for edit:", err);
      alert("Unable to edit this address. Please try again.");
    }
  };

  const handleDeleteAddress = async (event, address) => {
    event.stopPropagation();
    const addressId = resolveAddressId(address);
    if (!addressId || !user?.Vid) {
      alert("Address ID is missing. Please refresh and try again.");
      return;
    }
    if (!window.confirm("Delete this address?")) return;

    try {
      setDeletingAddressId(addressId);
      await api.GetDeleteCustomerAdd(user.Vid, addressId, "delete");

      setAdditionalAddresses((prev) => prev.filter((item) => getAddressId(item) !== addressId));

      const sessionAddresses = JSON.parse(sessionStorage.getItem("cartAdditionalAddresses") || "[]");
      sessionStorage.setItem(
        "cartAdditionalAddresses",
        JSON.stringify(sessionAddresses.filter((item) => getAddressId(item) !== addressId))
      );

      if (
        user?.selectedAdditionalAddress &&
        getAddressId(user.selectedAdditionalAddress) === addressId
      ) {
        const updatedUser = { ...user, selectedAdditionalAddress: null };
        setUser(updatedUser);
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.error("Failed to delete address:", err);
      alert("Unable to delete this address. Please try again.");
    } finally {
      setDeletingAddressId(null);
    }
  };

  // Check if customer is inactive
  useEffect(() => {
    const currentUser = user || JSON.parse(localStorage.getItem("user") || "{}");
    if (!currentUser?.Vid) return;

    const checkInactiveStatus = async () => {
      try {
        const res = await api.GetIndividualCustomerList(currentUser.Vid, "Existing User");
        
        const customerData =
          res?.data?.GetIndividualCustomerListjs?.Table?.[0] ||
          res?.GetIndividualCustomerListjs?.Table?.[0] ||
          null;

        if (Number(customerData?.IsActive) !== 1) {
          setIsCustomerInactive(true);
          alert("Your account is inactive. Please contact support.");
          navigate("/home");
        }
      } catch (err) {
        console.error("Failed to check inactive status:", err);
      }
    };

    checkInactiveStatus();
  }, []);

  useEffect(() => {
    const currentUser = user || JSON.parse(localStorage.getItem("user") || "{}");
    if (!currentUser?.Vid) return;

    const fetchAdditionalAddresses = async () => {
      try {
        const res = await api.GetMultipleAdd(currentUser.Vid);
        const data = res?.data?.GetMultipleAddjs || res?.GetMultipleAddjs || {};
        const savedAddresses = Array.isArray(data?.Table1) ? data.Table1 : [];
        const sessionAddresses = JSON.parse(sessionStorage.getItem("cartAdditionalAddresses") || "[]");
        setAdditionalAddresses(dedupeAddresses([...savedAddresses, ...sessionAddresses]));
      } catch (err) {
        console.error("Failed to fetch additional addresses:", err);
      }
    };

    fetchAdditionalAddresses();
  }, [user?.Vid]);

  useEffect(() => {
    if (!isOrderTypeOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOrderTypeOpen(false);
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOrderTypeOpen]);

  // Fetch and update user data from GetIndividualCustomerList
  useEffect(() => {
    const currentUser = user || JSON.parse(localStorage.getItem("user") || "{}");
    if (!currentUser?.Vid) return;

    const userType = currentUser.UserType || "Existing User";

    const fetchUserProfile = async () => {
      try {
        const res = await api.GetIndividualCustomerList(currentUser.Vid, userType);

        const customer =
          res?.data?.GetIndividualCustomerListjs?.Table?.[0] ||
          res?.GetIndividualCustomerListjs?.Table?.[0] ||
          null;
        
        const companyData =
          res?.data?.GetIndividualCustomerListjs?.Table1?.[0] ||
          res?.GetIndividualCustomerListjs?.Table1?.[0] ||
          null;

        if (customer) {
          const updatedUser = {
            Vid: customer.Vid ?? "",
            Vcode: customer.VCode ?? "",
            name: customer.Vname?.trim() || "",
            mail: customer.Mail?.trim() || "",
            address: currentUser.selectedAdditionalAddress?.AddressLine || customer.Address1?.trim() || "",
            landmark: currentUser.selectedAdditionalAddress?.Address2?.trim() || customer.Address2?.trim() || "", 
            area: currentUser.selectedAdditionalAddress?.City || customer.City?.trim() || "",
            division: currentUser.selectedAdditionalAddress?.Division || customer.Division?.trim() || "",
            Address1: customer.Address1?.trim() || currentUser.Address1 || "",
            Address2: customer.Address2?.trim() || currentUser.Address2 || "",
            City: customer.City?.trim() || currentUser.City || "",
            Division: customer.Division?.trim() || currentUser.Division || "",
            pin: (customer.Pincode || "").trim(),
            ShortName: customer.ShortName?.trim() || currentUser.ShortName || "",
            UserType: userType,
            IsCodAvail: customer.IsCodAvail ?? currentUser.IsCodAvail ?? 0,
            PinAllowed: customer.PinAllowed,
            selectedAdditionalAddress: currentUser.selectedAdditionalAddress,
            companyAddress: companyData?.ComAddress?.trim() || "No.5/25, Balaji Street,Arumbakkam,Opp. Police Quarters, Taluk Office Road,Sri Venkatesa Perumal Nagar, Chennai -600106.",
            companyName: companyData?.ComName?.trim() || "Hitham Associates Private Limited",
            companyEmail: companyData?.Email?.trim() || "annamatchennai@gmail.com",
            companyState: companyData?.State?.trim() || "TAMILNADU"
          };

          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.error("Failed to fetch user profile:", err);
      }
    };

    fetchUserProfile();
  }, []);

  // Check if COD is available
  const isCodAvailable = user?.IsCodAvail === 1;

  const totals = calculateTotals();
  const { subtotal, total, dailySubtotals } = totals;

  // Check if cart has any items
  const hasItems = cart && Object.values(cart).some(itemsObj =>
    Object.values(itemsObj).some(qty => qty > 0)
  );

  // Prefill delivery address
  useEffect(() => {
    if (user) {
      const selectedAddress = user.selectedAdditionalAddress?.AddressLine;
      const selectedCity = user.selectedAdditionalAddress?.City;
      const address = selectedAddress
        ? `${selectedAddress}${selectedCity ? ", " + selectedCity : ""}`
        : `${user?.Address1 || user.address || ""}${user?.city ? ", " + user.city : ""
        }`;
      setDeliveryAddress(address.trim());
    }
  }, [user]);

  // Auto-fill UPI amount
  useEffect(() => {
    if (paymentMethod === "upi" && (!amountPaid || parseFloat(amountPaid) === 0)) {
      setAmountPaid(total.toFixed(2));
    }
  }, [paymentMethod, total]);

  // Empty Cart View (must be after all hooks)
  if (!hasItems) {
    return (
      <div className="min-h-screen p-6">
        <div className="flex flex-col items-center justify-center max-w-md mx-auto mt-10 text-center">
          <div className="mb-6 text-6xl">🛒</div>
          <h2 className="mb-2 text-2xl font-bold text-gray-800">Your Cart is Empty</h2>
          <p className="mb-6 text-gray-500">Add some delicious items to get started!</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 font-semibold text-white transition-colors bg-red-500 rounded-lg shadow-lg hover:bg-red-600"
          >
            Order Items
          </button>
        </div>
      </div>
    );
  }

  const totalAmount = total;

  // const handlePayment = async () => {
  //   if (isSubmitting) return;
  //   setIsSubmitting(true);

  //   try {

  //     // 🟢 STEP 1: SAVE ORDER FIRST (Create DB Order)

  //     const basePayload = {
  //       UserName: user?.Vname || user?.name || "Guest",
  //       SessId: Date.now().toString(),
  //       Mobile: user?.Vcode || user?.mobile || "",
  //       Area: user?.city || user?.area || "",
  //       Division: user?.Division || user?.division || "",
  //       UserType: user?.UserType || "Existing User",
  //       PaymentRemark: paymentRemarks || "",
  //       DeliveryNote: deliveryRemarks || "",
  //       Vid: user?.Vid || 0,
  //       Vname: user?.Vname || user?.name || "",
  //       VCode: user?.Vcode || user?.mobile || "",
  //       Address: deliveryAddress,
  //       Paymode: "Pending",
  //       RazorpayOrderId: null,
  //       RazorpayPaymentId: null,
  //     };

  //     const allItems = [];

  //     for (const [date, itemsForDay] of Object.entries(cart)) {
  //       for (const [itemKey, qty] of Object.entries(itemsForDay)) {
  //         const item = window.__menuItemMap?.[itemKey];
  //         if (!item) throw new Error("Missing menu data. Please reload the menu and try again.");

  //         allItems.push({
  //           Vid: user?.Vid || 0,
  //           Vcode: user?.Vcode || "",
  //           Vname: user?.Vname || "",
  //           FoodType: item.type || "General",
  //           Itemid: item.id || itemKey,
  //           Item: item.Item_No || itemKey,
  //           item_desc: item.name || "",
  //           Qty: qty,
  //           notes: "",
  //           StandardCost: item.price || 0,
  //           DeliveryDate: date,
  //         });
  //       }
  //     }

  //     const saveRes = await api.SaveOnlineOrdersAPI({
  //       ...basePayload,
  //       DeliveryDate: new Date(),
  //       AmountPaid: totalAmount,
  //       Items: allItems,
  //     });

  //     const orderId = saveRes?.OrderId;
  //     const amount = saveRes?.Amount;

  //     if (!orderId) {
  //       alert("Failed to create order");
  //       setIsSubmitting(false);
  //       return;
  //     }

  //     // 🟢 STEP 2: CREATE RAZORPAY ORDER USING DB ORDER ID

  //     const razorRes = await api.CreateRazorpayOrder(
  //       {
  //       {
  //         orderId: orderId,
  //         amount: amount,
  //       }
  //     );

  //     const razorOrder = razorRes.data;

  //     // 🟢 STEP 3: OPEN RAZORPAY

  //     const options = {
  //       key: process.env.REACT_APP_RAZORPAY_KEY_ID,
  //   amount: razorOrder.amount,  
  //       currency: "INR",
  //       name: "Annam Foods",
  //       description: "Food Order Payment",
  //     order_id: razorOrder.id, 

  //       handler: async function (response) {

  //         // 🟢 STEP 4: UPDATE PAYMENT IN DB

  //         await api.UpdateRazorPay(
  //           {
  //           {
  //             OrderId: orderId,
  //             RazorpayOrderId: response.razorpay_order_id,
  //             RazorpayPaymentId: response.razorpay_payment_id,
  //           }
  //         );

  //         alert("✅ Payment Successful & Order Placed!");
  //         navigate("/confirmation");
  //       },

  //       theme: { color: "#E4002B" },
  //     };

  //     const rzp = new window.Razorpay(options);
  //     rzp.open();

  //   } catch (error) {
  //     console.log(error);
  //     alert("Unable to initiate payment");
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

const handleCodOrder = async () => {
  if (isSubmitting) return;
  setIsSubmitting(true);

  try {
    // Check if there's an existing OrderId in session (for retry after failed order)
    const existingOrderId = sessionStorage.getItem("pendingOrderId");
    
    const basePayload = {
      UserName: user?.Vname || user?.name || "Guest",
      SessId: Date.now().toString(),
      Mobile: user?.Vcode || user?.mobile || "",
      Area: user?.city || user?.area || "",
      Division: user?.Division || user?.division || "",
      UserType: user?.UserType || "Existing User",
      PaymentRemark: paymentRemarks || "",
      DeliveryNote: deliveryRemarks || "",
      Vid: user?.Vid || 0,
      Vname: user?.Vname || user?.name ||  "",
      VCode: user?.Vcode || "",
      Address: deliveryAddress,
      Paymode: "cod",
      RazorpayOrderId: null,
      RazorpayPaymentId: null,
      OId: existingOrderId ? parseInt(existingOrderId) : 0,
      Mode: "Web",
      IsTakeAway: isTakeaway ? 1 : 0,
    };

    const allItems = [];

     for (const [date, itemsForDay] of Object.entries(cart)) {
       for (const [itemKey, qty] of Object.entries(itemsForDay)) {
         const item = window.__menuItemMap?.[itemKey];
         if (!item) {
           throw new Error(`Missing menu data for item ${itemKey}. Please reload the menu and try again.`);
         }

         // Calculate price dynamically based on current delivery mode and user details
         let regularPrice;
         if (user?.selectedAdditionalAddress && user?.division === "Circle1") {
           regularPrice = Number(item.raw?.Circle1Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.selectedAdditionalAddress && user?.division === "Circle2") {
           regularPrice = Number(item.raw?.Circle2Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.selectedAdditionalAddress && user?.division === "Circle3") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.UserType === "New User") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle1") {
           regularPrice = Number(item.raw?.Circle1Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle2") {
           regularPrice = Number(item.raw?.Circle2Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle3") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         }

         const takeawayPrice = Number(item.raw?.TakeAway ?? item.takeawayPrice ?? regularPrice) || 0;
         const price = isTakeaway ? takeawayPrice : regularPrice;

         allItems.push({
           Vid: user?.Vid || 0,
           Vcode: user?.Vcode || "",
           Vname: user?.Vname || user?.name || "",
           FoodType: item.type || "General",
           Itemid: item.id || itemKey,
           Item: item.Item_No || itemKey,
           item_desc: item.name || "",
           Qty: qty,
           notes: "",
           StandardCost: price || 0,
           DeliveryDate: date,
         });
       }
     }

    const saveRes = await api.SaveOnlineOrdersAPI({
      ...basePayload,
      DeliveryDate: new Date(),
      AmountPaid: totalAmount,   // ✅ FIXED
      Items: allItems,
    });

    console.log("COD Save Response:", saveRes);

   if (saveRes?.Status !== 1) {
  alert("Failed to create order");
  return;
}

// ✅ Clear the pending OrderId from session after successful order
sessionStorage.removeItem("pendingOrderId");

// ✅ ADD THIS (same as Razorpay flow)
placeOrder({
  distanceKey,
  paymentMethod: "cod",
  address: deliveryAddress,
  remarks: deliveryRemarks,
  referenceNo: "",
  amountPaid: totalAmount,
  apiOrderId: saveRes?.OrderId,
});

alert("✅ Order Placed Successfully (Cash on Delivery)");
navigate("/confirmation");
  } catch (error) {
    console.error("COD ERROR:", error);
    alert("Failed to place order");
  } finally {
    setIsSubmitting(false);
  }
};

const handlePayment = async () => {
  if (isSubmitting) return;
  setIsSubmitting(true);

  try {
    // 🟢 STEP 1: SAVE ORDER FIRST (Create DB Order)
    
    // Check if there's an existing OrderId in session (for retry after failed payment)
    const existingOrderId = sessionStorage.getItem("pendingOrderId");
    
    const basePayload = {
      UserName: user?.Vname || user?.name || "Guest",
      SessId: Date.now().toString(),
      Mobile: user?.Vcode || user?.mobile || "",
      Area: user?.city || user?.area || "",
      Division: user?.Division || user?.division || "",
      UserType: user?.UserType || "Existing User",
      PaymentRemark: paymentRemarks || "",
      DeliveryNote: deliveryRemarks || "",
      Vid: user?.Vid || 0,
      Vname: user?.Vname || user?.name || "",
      VCode: user?.Vcode || user?.mobile || "",
      Address: deliveryAddress,
      Paymode: "Pending",
      RazorpayOrderId: null,
      RazorpayPaymentId: null,
      OId: existingOrderId ? parseInt(existingOrderId) : 0,
      Mode: "Web",
      IsTakeAway: isTakeaway ? 1 : 0,
    };

    const allItems = [];

     for (const [date, itemsForDay] of Object.entries(cart)) {
       for (const [itemKey, qty] of Object.entries(itemsForDay)) {
         const item = window.__menuItemMap?.[itemKey];
         if (!item) {
           throw new Error(`Missing menu data for item ${itemKey}. Please reload the menu and try again.`);
         }

         // Calculate price dynamically based on current delivery mode and user details
         let regularPrice;
         if (user?.selectedAdditionalAddress && user?.division === "Circle1") {
           regularPrice = Number(item.raw?.Circle1Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.selectedAdditionalAddress && user?.division === "Circle2") {
           regularPrice = Number(item.raw?.Circle2Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.selectedAdditionalAddress && user?.division === "Circle3") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.UserType === "New User") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle1") {
           regularPrice = Number(item.raw?.Circle1Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle2") {
           regularPrice = Number(item.raw?.Circle2Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle3") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         }

         const takeawayPrice = Number(item.raw?.TakeAway ?? item.takeawayPrice ?? regularPrice) || 0;
         const price = isTakeaway ? takeawayPrice : regularPrice;

         allItems.push({
           Vid: user?.Vid || 0,
           Vcode: user?.Vcode || "",
           Vname: user?.Vname || user?.name || "",
           FoodType: item.type || "General",
           Itemid: item.id || itemKey,
           Item: item.Item_No || itemKey,
           item_desc: item.name || "",
           Qty: qty,
           notes: "",
           StandardCost: price || 0,
           DeliveryDate: date,
         });
       }
     }

    const saveRes = await api.SaveOnlineOrdersAPI({
      ...basePayload,
      DeliveryDate: new Date(),
      AmountPaid: totalAmount,
      Items: allItems,
    });

    const orderId = saveRes?.OrderId;
    const amount = saveRes?.Amount;

    if (!orderId) {
      alert("Failed to create order");
      setIsSubmitting(false);
      return;
    }

    // Store OrderId in session for retry scenarios
    sessionStorage.setItem("pendingOrderId", orderId.toString());

    // 🟢 STEP 2: CREATE RAZORPAY ORDER

    if (!window.Razorpay) {
      alert("Payment service is not available. Please try again later.");
      setIsSubmitting(false);
      return;
    }

    const razorpayKey = await getRazorpayKeyId();
    if (!razorpayKey) {
      alert("Payment configuration is missing. Please contact support.");
      setIsSubmitting(false);
      return;
    }

    const razorOrder = await api.CreateRazorpayOrder({
      orderId: orderId,
      amount: amount,
    });

    // 🟢 STEP 3: OPEN RAZORPAY

    const options = {
      key: razorpayKey,
      amount: razorOrder.amount,
      currency: "INR",
      name: "Annam Foods",
      description: "Food Order Payment",
      order_id: razorOrder.id,

handler: async function (response) {
  try {

    // 1️⃣ Verify Payment
    const verifyRes = await api.VerifyPayment({
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
      localOrderId: orderId
    });

    console.log("Verify Response:", verifyRes);

    // ✅ Now check status from verify response
    if (verifyRes.status === "captured") {
  const paymentMethod = verifyRes.method; // upi / card
      // 2️⃣ Update Razorpay Data in DB
      const updatePayload = {
          OId: orderId,
          RazorpayOrderId: response.razorpay_order_id,
          RazorpayPaymentid: response.razorpay_payment_id,
          RazorpaySignature: response.razorpay_signature,
          Razorpaystatus: verifyRes.status,
          RPAttempts: verifyRes.attempts,
          RPCreated_at: verifyRes.created_at ? new Date(verifyRes.created_at * 1000) : new Date(),
          Paymode: paymentMethod 
        };

      try {
        await api.UpdateRazorPay(updatePayload);
      } catch (updateErr) {
        console.error("Payment captured but update failed", updateErr);
        sessionStorage.setItem("paymentUpdatePending", JSON.stringify(updatePayload));
        alert("Payment received, but order confirmation update is pending. Please contact support with your payment id.");
        setIsSubmitting(false);
        return;
      }

      // 3️⃣ Update context like handlePlaceOrder
      placeOrder({
        distanceKey,
        paymentMethod: paymentMethod,
        address: deliveryAddress,
        remarks: deliveryRemarks,
        referenceNo: response.razorpay_payment_id,
        amountPaid: totalAmount,
        apiOrderId: orderId,
      });

      // ✅ Clear the pending OrderId from session after successful payment
      sessionStorage.removeItem("pendingOrderId");

      alert("✅ Payment Successful & Order Confirmed!");
      navigate("/confirmation");
      setIsSubmitting(false);

    } else {
      alert("Payment not completed.");
      setIsSubmitting(false);
    }

  } catch (err) {
    console.error("Verification failed", err);
    alert("Payment verification failed.");
    setIsSubmitting(false);
  }
},
      modal: {
        ondismiss: async function () {
          try {
            await api.UpdateRazorPay({
              OId: orderId,
              Razorpaystatus: "cancelled",
              Paymode: "Cancelled",
            });
          } catch (cancelErr) {
            console.error("Failed to mark payment cancelled", cancelErr);
          } finally {
            sessionStorage.removeItem("pendingOrderId");
            setIsSubmitting(false);
          }
        },
      },
      theme: { color: "#E4002B" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

  } catch (error) {
    console.log(error);
    alert(error.message || "Unable to initiate payment");
    setIsSubmitting(false);
  }
};

  // PLACE ORDER
  const handlePlaceOrder = async (razorpayData = null) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (paymentMethod === "upi" && (!amountPaid || parseFloat(amountPaid) <= 0)) {
      alert("Please enter a valid Amount Paid greater than 0.");
      setIsSubmitting(false);
      return;
    }

    // Check if there's an existing OrderId in session (for retry after failed order)
    const existingOrderId = sessionStorage.getItem("pendingOrderId");

    const basePayload = {
      UserName: user?.Vname || user?.name || "Guest",
      SessId: Date.now().toString(),
      Mobile: user?.Vcode || user?.mobile || "",
      Area: user?.city || user?.area || "",
      Division: user?.Division || user?.division || "",
      UserType: user?.UserType || "Existing User",
      PaymentRemark: paymentRemarks || "",
      DeliveryNote: deliveryRemarks || "",
      Vid: user?.Vid || 0,
      Vname: user?.Vname || user?.name || "",
      VCode: user?.Vcode || user?.mobile || "",
      Address: deliveryAddress,
      Paymode: paymentMethod === "upi" ? "UPI" : "COD",
      RazorpayOrderId: razorpayData?.razorpay_order_id || null,
      RazorpayPaymentId: razorpayData?.razorpay_payment_id || null,
      OId: existingOrderId ? parseInt(existingOrderId) : 0,
      Mode: existingOrderId ? "Edit" : "New",
      IsTakeAway: isTakeaway ? 1 : 0,
    };
    const allItems = [];

     for (const [date, itemsForDay] of Object.entries(cart)) {
       for (const [itemKey, qty] of Object.entries(itemsForDay)) {
         const item = window.__menuItemMap?.[itemKey];
         if (!item) {
           throw new Error(`Missing menu data for item ${itemKey}. Please reload the menu and try again.`);
         }

         // Calculate price dynamically based on current delivery mode and user details
         let regularPrice;
         if (user?.selectedAdditionalAddress && user?.division === "Circle1") {
           regularPrice = Number(item.raw?.Circle1Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.selectedAdditionalAddress && user?.division === "Circle2") {
           regularPrice = Number(item.raw?.Circle2Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.selectedAdditionalAddress && user?.division === "Circle3") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.UserType === "New User") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle1") {
           regularPrice = Number(item.raw?.Circle1Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle2") {
           regularPrice = Number(item.raw?.Circle2Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else if (user?.division === "Circle3") {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         } else {
           regularPrice = Number(item.raw?.Circle3Rate ?? item.raw?.MRP ?? item.raw?.StandardCost ?? item.regularPrice ?? 0) || 0;
         }

         const takeawayPrice = Number(item.raw?.TakeAway ?? item.takeawayPrice ?? regularPrice) || 0;
         const price = isTakeaway ? takeawayPrice : regularPrice;

         console.log('Cart handlePlaceOrder: itemKey =', itemKey, 'userType =', user?.UserType, 'division =', user?.division, 'isTakeaway =', isTakeaway, 'calculated price =', price);

         allItems.push({
           Vid: user?.Vid || 0,
           Vcode: user?.Vcode || "",
           Vname: user?.Vname || user?.name || "",
           UserType: user?.UserType || "Existing User",
           FoodType: item.type || "General",
           Itemid: item.id || itemKey,
           Item: item.Item_No || itemKey,
           item_desc: item.name || "Unknown",
           Qty: qty,
           notes: "",
           StandardCost: price || 0,
           DeliveryDate: date,
         });
       }
     }

    try {
      const grouped = {};
      allItems.forEach((itm) => {
        const key = `${itm.DeliveryDate}_${itm.FoodType}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(itm);
      });

      // Send API call per group
      for (const [key, items] of Object.entries(grouped)) {
        const [date, mealType] = key.split("_");
        const dailySubtotal = items.reduce((sum, itm) => sum + itm.StandardCost * itm.Qty, 0);

        const payload = {
          ...basePayload,
          DeliveryDate: date,
          AmountPaid: parseFloat(dailySubtotal.toFixed(2)),
          Items: items,
        };

        try {
          const res = await api.SaveOnlineOrdersAPI(payload);
          // Store OrderId in session for retry scenarios
          if (res?.OrderId) {
            sessionStorage.setItem("pendingOrderId", res.OrderId.toString());
          }
          if (!res?.SaveOnlineOrders) {
            alert(`Failed to save order for ${date} (${mealType}).`);
          }
        } catch (err) {
          alert(`Error saving order for ${date} (${mealType}).`);
        }
      }

      // ✅ Clear the pending OrderId from session after successful order
      sessionStorage.removeItem("pendingOrderId");

      // Update context and navigate
      placeOrder({
        distanceKey,
        paymentMethod,
        address: deliveryAddress,
        remarks: deliveryRemarks,
        referenceNo,
        amountPaid: parseFloat(amountPaid || 0),
      });

      alert("✅ Order has been submitted Successfully!");
      navigate("/confirmation");
    } catch (err) {
      alert("Something went wrong while placing your order.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };


  // DAILY SUMMARY
  const dailySummary = Object.entries(cart || {}).map(([date, itemsObj]) => {
    const itemList = Object.entries(itemsObj).map(([itemId, qty]) => {
      const item = findMenuItem(itemId);
      if (!item) return null;
      return (
        <li key={itemId} className="flex items-center justify-between px-3 py-2 mb-2 rounded-lg bg-gray-50">
          <div className="flex-1">
            <span className="text-sm font-medium text-gray-800">{item.name}</span>
            <p className="text-xs text-gray-500">₹{item.price.toFixed(2)} each</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center overflow-hidden border border-gray-300 rounded-lg">
              <button
                onClick={() => addToCart(date, itemId, -1)}
                className="px-3 py-1 text-red-600 transition-colors border-r border-gray-300 bg-red-50 hover:bg-red-100"
              >
                −
              </button>
              <span className="px-3 py-1 font-medium text-gray-800 min-w-[30px] text-center">{qty}</span>
              <button
                onClick={() => addToCart(date, itemId, 1)}
                className="px-3 py-1 text-green-600 transition-colors border-l border-gray-300 bg-green-50 hover:bg-green-100"
              >
                +
              </button>
            </div>
            <span className="font-semibold text-gray-800 min-w-[70px] text-right">₹{(item.price * qty).toFixed(2)}</span>
          </div>
        </li>
      );
    }).filter(Boolean);

    if (!itemList.length) return null;

    return (
      <div key={date} className="pt-2 pb-2 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-700">{formatDate(date)}</p>
        <ul className="pl-2 mt-1 space-y-1 text-sm">{itemList}</ul>
        <div className="flex justify-between mt-1 text-sm font-medium">
          <span>Subtotal:</span>
          <span>₹{dailySubtotals?.[date]?.toFixed(2)}</span>
        </div>
      </div>
    );
  });

  const primaryShortName = user?.ShortName?.trim?.() || "Primary";
  const primaryAddress = {
    Vid: user?.Vid,
    ShortName: primaryShortName,
    Address1: user?.Address1 || (!user?.selectedAdditionalAddress ? user?.address : "") || "",
    Address2: user?.Address2 || (!user?.selectedAdditionalAddress ? user?.landmark : "") || "",
    City: user?.City || user?.city || (!user?.selectedAdditionalAddress ? user?.area : "") || "",
    Division: user?.Division || (!user?.selectedAdditionalAddress ? user?.division : "") || "",
    AreaId: user?.AreaId || user?.AreaID || user?.areaId,
    IsCodAvail: user?.IsCodAvail,
  };
  const primaryAddressKey = getAddressKey({ ...primaryAddress, ShortName: "Primary" }, 0, "primary");
  const isPrimarySelected =
    !user?.selectedAdditionalAddress || user?.selectedAdditionalAddress?.selectedKey === primaryAddressKey;

  const renderAddressCard = (address, index, isPrimary = false) => {
    const addressKey = isPrimary ? primaryAddressKey : getAddressKey(address, index);
    const isSelected = isPrimarySelected && isPrimary
      ? true
      : user?.selectedAdditionalAddress?.selectedKey === addressKey;
    const addressLine = getAddressLine(address);
    const city = getAddressCity(address);
    const division = getAddressDivision(address);

    const addressId = getAddressId(address);
    const canManageAddress = !isPrimary;

    return (
      <div
        key={addressKey}
        onClick={() => applySelectedAddress(address, addressKey, isPrimary)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            applySelectedAddress(address, addressKey, isPrimary);
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        className={`w-full p-3 text-left transition-all border rounded-xl ${
          isSelected
            ? "border-red-400 bg-red-50 shadow-sm ring-2 ring-red-100"
            : "border-gray-200 bg-white hover:border-red-200 hover:bg-red-50/40"
        }`}
      >
        <div className="flex items-start gap-3">
          <span className={`inline-flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg border ${
            isSelected ? "text-red-600 bg-white border-red-200" : "text-gray-500 bg-gray-50 border-gray-200"
          }`}>
            {isPrimary ? <HomeIcon size={17} /> : <MapPin size={17} />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-bold text-gray-900">{address.ShortName || `Address ${index + 1}`}</h4>
              <div className="flex items-center gap-1.5">
                {isSelected && (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 text-green-600 border border-green-200 rounded-full shadow-sm bg-green-50"
                    aria-label="Selected address"
                    title="Selected address"
                  >
                    <CheckCircle size={15} strokeWidth={2.5} />
                  </span>
                )}
                {canManageAddress && (
                  <>
                    <button
                      type="button"
                      onClick={(event) => handleEditAddress(event, address)}
                      className="inline-flex items-center justify-center text-blue-600 transition-colors border border-blue-100 rounded-full w-7 h-7 bg-blue-50 hover:bg-blue-100"
                      aria-label={`Edit ${address.ShortName || "address"}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => handleDeleteAddress(event, address)}
                      disabled={deletingAddressId === addressId}
                      className="inline-flex items-center justify-center text-red-600 transition-colors border border-red-100 rounded-full w-7 h-7 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      aria-label={`Delete ${address.ShortName || "address"}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-sm font-medium leading-relaxed text-gray-800">
              {[addressLine, city].filter(Boolean).join(", ") || "Address not available"}
            </p>
            {division && (
              <p className="mt-1 text-xs font-medium leading-relaxed text-gray-500">{division}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative max-w-3xl p-3 mx-auto sm:p-6 animate-fade-in">
      {/* Floating Takeaway Button */}
      {/* <div className="fixed z-50 top-20 right-6">
        <button
          onClick={() => setIsTakeaway(!isTakeaway)}
          className={`text-sm font-bold px-4 py-2 rounded-full transition-all duration-300 border-3 shadow-xl ${isTakeaway ? 'bg-green-600 text-white border-green-700 hover:bg-green-700' : 'bg-white text-red-600 border-white/30 hover:bg-gray-100'}`}
        >
          {isTakeaway ? 'TAKEAWAY: ON' : 'TAKEAWAY: OFF'}
        </button>
      </div> */}

      <div className="overflow-hidden bg-white border border-gray-100 shadow-lg rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 text-white bg-red-500">
          <h3 className="text-lg font-semibold sm:text-xl">Your Order Summary</h3>
          <button onClick={() => navigate("/home")} className="text-2xl font-bold hover:text-gray-200">&times;</button>
        </div>

        <div className="p-4 space-y-5 sm:p-6">
          {/* User Info */}
          <div className="p-4 space-y-3 border bg-gray-50 sm:p-5 rounded-xl">
            {/* <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-gray-700">{user?.Vname || user?.name || "Guest"} | {user?.Vcode || ""}</p>
              {!isTakeaway && (
                <button
                  type="button"
                  onClick={() => setIsAddressModalOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-red-700 transition-colors bg-white border border-red-200 rounded-full shadow-sm hover:bg-red-50"
                >
                  <MapPin size={14} />
                  Select Address
                  <span className="flex items-center justify-center min-w-[20px] h-5 px-1 text-[11px] text-white bg-red-500 rounded-full">
                    {additionalAddresses.length + 1}
                  </span>
                </button>
              )}
            </div> */}
            <div>
              <label className="text-xs font-medium text-gray-600 sm:text-sm">Delivery Address</label>
              <textarea
                rows="2"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                readOnly
                className="w-full p-2 mt-1 text-sm border rounded-md focus:ring-1 focus:ring-red-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 sm:text-sm">Delivery Remarks</label>
              <textarea
                rows="2"
                value={deliveryRemarks}
                onChange={(e) => setDeliveryRemarks(e.target.value)}
                placeholder="E.g. Leave at security gate"
                className="w-full p-2 mt-1 text-sm border rounded-md focus:ring-1 focus:ring-red-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 sm:text-sm">Payment Remarks</label>
              <textarea
                rows="2"
                value={paymentRemarks}
                onChange={(e) => setPaymentRemarks(e.target.value)}
                placeholder="E.g. Send UPI QR before delivery"
                className="w-full p-2 mt-1 text-sm border rounded-md focus:ring-1 focus:ring-red-400"
              />
            </div>
          </div>

          {/* Items Summary */}
          <div className="p-3 bg-white border rounded-xl sm:p-4">

  <div className="flex items-center justify-between mb-2">
    <h4 className="text-sm font-semibold text-gray-700 sm:text-base">
      Items Ordered
    </h4>

  



       {/* <div className="flex items-center p-1 bg-gray-100 rounded-full w-fit">
  <button
    onClick={() => setIsTakeaway(false)}
    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${
      !isTakeaway
        ? "bg-yellow-400 text-black shadow"
        : "text-gray-600"
    }`}
  >
    🛵 Delivery
  </button>

  <button
    onClick={() => setIsTakeaway(true)}
    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${
      isTakeaway
        ? "bg-teal-500 text-white shadow"
        : "text-gray-600"
    }`}
  >
    🛍️ Takeaway
  </button>
</div> */}


  </div>

  <div className="max-h-[220px] sm:max-h-[300px] overflow-y-auto pr-1 space-y-2">
    {dailySummary}
  </div>

</div>

          {/* Totals */}
          <div className="p-4 space-y-2 text-sm border bg-gray-50 rounded-xl sm:text-base">
            <div className="flex justify-between"><span>Total Items Cost:</span><span>₹{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between pt-2 text-base font-bold border-t"><span>Grand Total:</span><span>₹{total.toFixed(2)}</span></div>
<div className="mt-2">
  <div className="flex items-center justify-between gap-3">

    {/* Description */}
    <div
      className={`text-xs px-3 py-2 rounded-md ${
        isTakeaway
          ? "bg-teal-50 text-teal-700 border border-teal-200"
          : "bg-red-50 text-red-700 border border-red-200"
      }`}
    >
      {isTakeaway
        ? "Pick it up fresh and ready from our kitchen."
        : "Stay comfy. We'll bring the goodness straight to your door."}
    </div>

    {/* Button */}
    <button
      type="button"
      onClick={() => setIsOrderTypeOpen(true)}
      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all duration-300 shadow-sm whitespace-nowrap ${
        isTakeaway
          ? "bg-teal-500 text-white hover:bg-teal-600"
          : "bg-gray-100 text-red-600 hover:bg-gray-200"
      }`}
      aria-haspopup="dialog"
      aria-expanded={isOrderTypeOpen}
    >
      {isTakeaway ? "TAKEAWAY 🛍️" : "DELIVERY 🛵"}
      <ChevronDown size={14} strokeWidth={2.5} />
    </button>

  </div>
</div>
          </div>

           {/* Payment Method */}
           <div>
             <h4 className="mb-2 font-semibold text-gray-700">Payment Method</h4>
             <div className="flex flex-col gap-2 sm:flex-row">
               {isCodAvailable && (
                 <button
                   // onClick={paymentMethod === "cod" ? handleCodOrder : handlePayment}
                    onClick={handleCodOrder}
                    disabled={isSubmitting || isCustomerInactive}
                    className={`flex-1 py-2 rounded-md text-sm font-medium border ${isSubmitting || isCustomerInactive ? "bg-gray-200 text-gray-400 cursor-not-allowed" : paymentMethod === "cod" ? "bg-red-100 border-red-400" : "bg-gray-50"}`}
                  >
                    {isSubmitting ? "Placing..." : "Cash on Delivery"}
                 </button>
               )}
               <button
                 onClick={handlePayment}
                 disabled={isSubmitting || (paymentMethod === "upi" && parseFloat(amountPaid) <= 0) || isCustomerInactive}
                 className={`flex-1 py-2 rounded-md text-sm font-medium border ${isSubmitting || (paymentMethod === "upi" && parseFloat(amountPaid) <= 0) || isCustomerInactive ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-red-500 border-red-500 text-white"}`}
               >
                 Make Payment
               </button>
             </div>
           </div>

          {/* UPI Section */}
          {/* {paymentMethod === "upi" && (
            <div className="p-4 mt-4 text-center border-2 border-dashed rounded-xl">
              <h3 className="font-semibold text-gray-700">Scan QR to Complete Payment</h3>

              <div className="mt-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
                    `upi://pay?pa=${user?.Upicode || user?.Address1 || "default@upi"}&pn=${encodeURIComponent(user?.Vname || "Annam Home Foods")}&am=${total.toFixed(2)}&cu=INR&tn=${encodeURIComponent("Order from " + (user?.Vcode || "Guest"))}`
                  )}`}
                  alt="UPI QR Code"
                  className="mx-auto rounded-lg shadow-md"
                />
                <p className="mt-2 text-xs text-gray-500">Use GPay, PhonePe, or Paytm to scan & pay</p>
              </div>

              <div className="mt-3">
                <button
                  onClick={() => {
                    const upiUrl = `upi://pay?pa=${user?.Upicode || user?.Address1 || "default@upi"}&pn=${encodeURIComponent(user?.Vname || "Annam Home Foods")}&am=${total.toFixed(2)}&cu=INR&tn=${encodeURIComponent("Order from " + (user?.Vname || user?.name || "Guest"))}`;
                    window.location.href = upiUrl;
                  }}
                  className="px-4 py-2 text-sm font-medium text-white transition-colors bg-green-500 rounded-lg hover:bg-green-600"
                >
                  Open in UPI App (Mobile)
                </button>
                <p className="mt-1 text-xs text-gray-500">Tap to open GPay, PhonePe, or Paytm directly</p>
              </div>

              <div className="flex flex-col gap-3 mt-3 text-left sm:flex-row">
                <div className="flex-1 sm:flex-[0.6]">
                  <label className="text-xs font-medium text-gray-600 sm:text-sm">Ref. No.</label>
                  <input
                    type="text"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                    placeholder="UPI Ref #"
                    className="w-full p-2 mt-1 text-sm border rounded-md focus:ring-1 focus:ring-red-400"
                  />
                </div>
                <div className="flex-1 sm:flex-[0.4]">
                  <label className="text-xs font-medium text-gray-600 sm:text-sm">Amount Paid</label>
                  <input
                    type="number"
                    readOnly
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="₹"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    className="w-full p-2 mt-1 text-sm border rounded-md focus:ring-1 focus:ring-red-400"
                  />
                </div>
              </div>
            </div>
          )} */}


        </div>
      </div>
      {isOrderTypeOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
          onClick={() => setIsOrderTypeOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-order-type-title"
            className="w-full max-w-sm overflow-hidden bg-white shadow-2xl rounded-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-bold tracking-wide uppercase text-white/80">Order preference</p>
                <h3 id="cart-order-type-title" className="text-lg font-bold">How would you like your order?</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsOrderTypeOpen(false)}
                className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full hover:bg-white/15"
                aria-label="Close order type selector"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-3 p-5 sm:grid-cols-2">
              <button
                type="button"
                onClick={selectTakeaway}
                className={`flex flex-col items-center gap-2 p-5 text-center transition-all border-2 rounded-xl hover:-translate-y-0.5 hover:shadow-md ${
                  isTakeaway ? "border-teal-500 bg-teal-50" : "border-gray-200 bg-white hover:border-teal-300"
                }`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 text-teal-700 bg-teal-100 rounded-full">
                  <ShoppingBag size={23} />
                </span>
                <span className="font-bold text-gray-900">Takeaway</span>
                <span className="text-xs leading-relaxed text-gray-500">Pick up your order and apply takeaway rates.</span>
              </button>

              <button
                type="button"
                onClick={selectDelivery}
                className={`flex flex-col items-center gap-2 p-5 text-center transition-all border-2 rounded-xl hover:-translate-y-0.5 hover:shadow-md ${
                  !isTakeaway ? "border-red-400 bg-red-50" : "border-gray-200 bg-white hover:border-red-300"
                }`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 text-red-600 bg-red-100 rounded-full">
                  <Truck size={23} />
                </span>
                <span className="font-bold text-gray-900">Delivery</span>
                <span className="text-xs leading-relaxed text-gray-500">Choose an address and apply its delivery rate.</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {isAddressModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm">
          <div className="w-full max-w-xl overflow-hidden bg-white shadow-2xl rounded-xl">
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-bold tracking-wide uppercase text-white/80">Delivery Address</p>
                <h3 className="text-base font-bold">Choose Address</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddressModalOpen(false)}
                className="inline-flex items-center justify-center w-8 h-8 text-white transition-colors rounded-full hover:bg-white/15"
                aria-label="Close address selector"
              >
                <X size={17} />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh]">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/add-additional-address")}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-red-700 transition-colors border border-red-300 border-dashed rounded-lg bg-red-50 hover:bg-red-100"
                >
                  <Plus size={16} />
                  Add Additional Address
                </button>
              </div>

              {renderAddressCard(primaryAddress, 0, true)}
              {additionalAddresses.map((address, index) => renderAddressCard(address, index))}
            </div>
          </div>
        </div>
      )}
      {addressRestrictionModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm">
          <div className="w-full max-w-2xl overflow-hidden bg-white shadow-2xl rounded-2xl">
            <div className="flex items-center justify-between gap-3 px-5 py-3 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-black tracking-wide uppercase text-white/85">Delivery Restriction</p>
                <h3 className="text-lg font-black">Some cart items cannot be delivered</h3>
              </div>
              <button
                type="button"
                onClick={() => setAddressRestrictionModal(null)}
                className="inline-flex items-center justify-center w-8 h-8 text-white transition-colors rounded-full hover:bg-white/15"
                aria-label="Close delivery warning"
              >
                <X size={17} />
              </button>
            </div>
            <div className="p-3 space-y-3">
              <div className="p-3 border border-red-200 rounded-xl bg-red-50">
                <h4 className="text-base font-black text-red-700">
                  {addressRestrictionModal.wording?.header || "Heavy rain in"} {addressRestrictionModal.addressArea || addressRestrictionModal.addressName}
                </h4>
                <p className="mt-1 text-sm font-medium leading-relaxed text-red-900">
                  {addressRestrictionModal.blockedMealTypes.map(formatMealType).join(", ")} {addressRestrictionModal.wording?.content || "delivery is temporarily suspended for this address. Your selected address and cart were not changed."}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {MEAL_TYPES.map((mealType) => {
                    const isBlocked = addressRestrictionModal.blockedMealTypes.includes(mealType);
                    return (
                      <span
                        key={mealType}
                        className={`px-3 py-1 text-xs font-black rounded-full ${
                          isBlocked ? "text-red-700 bg-red-100" : "text-green-700 bg-green-100"
                        }`}
                      >
                        {isBlocked ? "✕" : "✓"} {formatMealType(mealType)} {isBlocked ? "unavailable" : "available"}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-hidden border border-gray-200 rounded-xl">
                <div className="px-4 py-2 bg-gray-50">
                  <h4 className="font-black text-gray-800">Affected cart items</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {addressRestrictionModal.affectedItems.map((item) => (
                    <div key={`${item.date}-${item.itemId}`} className="flex items-center justify-between gap-3 px-4 py-2 bg-white">
                      <div>
                        <p className="text-sm font-black text-gray-900">{item.name}</p>
                        <p className="text-xs font-semibold text-gray-500">{formatMealType(item.mealType).toUpperCase()} - {formatDate(item.date)} - Qty {item.qty}</p>
                      </div>
                      <p className="text-sm font-black text-gray-900">
                        ₹{(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={removeBlockedItemsAndUseAddress}
                  className="w-full px-3 py-2.5 text-sm font-black text-white transition-colors bg-red-500 rounded-xl hover:bg-red-600"
                >
                  Remove {Array.from(new Set(addressRestrictionModal.affectedItems.map((item) => formatMealType(item.mealType)))).join(", ")} Items & Use This Address
                </button>
                <button
                  type="button"
                  onClick={() => setAddressRestrictionModal(null)}
                  className="w-full px-3 py-2.5 text-sm font-black text-gray-700 transition-colors bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
                >
                  Keep Current Address
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddressRestrictionModal(null);
                    setIsAddressModalOpen(true);
                  }}
                  className="w-full px-3 py-2.5 text-sm font-black text-red-700 transition-colors border border-red-200 rounded-xl bg-red-50 hover:bg-red-100"
                >
                  Choose Another Address
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
